import {
  type AccountInfo, InteractionRequiredAuthError, PublicClientApplication
} from '@azure/msal-browser';
import { BASE_SCOPES, CLIENT_ID, SIGN_IN_TENANT } from './config';

/** Microsoft sign-in: any work or school account in production, one organization in test. */
export const msal: PublicClientApplication = new PublicClientApplication({
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${SIGN_IN_TENANT}`,
    redirectUri: window.location.origin
  },
  // Shared across tabs: a sign-in that returns in another tab (or after a browser restart)
  // still finds the request it belongs to.
  cache: { cacheLocation: 'localStorage' }
});

export async function initAuth(): Promise<AccountInfo | undefined> {
  await msal.initialize();
  let result: Awaited<ReturnType<PublicClientApplication['handleRedirectPromise']>> = null;
  try {
    result = await msal.handleRedirectPromise();
  } catch (error) {
    // A stale or orphaned sign-in response (e.g. a refreshed #code=… address). Drop it and
    // carry on: the user is either still signed in, or simply sees the sign-in page again.
    console.warn('[SharePointOrganizer] Ignoring an incomplete sign-in response.', error);
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }
  const account: AccountInfo | undefined = result?.account ?? msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (account) {
    msal.setActiveAccount(account);
  }
  return account;
}

export function signIn(): Promise<void> {
  return msal.loginRedirect({ scopes: BASE_SCOPES, prompt: 'select_account' });
}

export function signOut(): Promise<void> {
  return msal.logoutRedirect({ account: msal.getActiveAccount() ?? undefined });
}

/**
 * Access token for the given scopes; asks the user (popup) only when consent or sign-in is
 * needed and `interactive` allows it — popups must come from a click, or browsers block them.
 */
export async function getToken(scopes: string[], interactive: boolean = true): Promise<string> {
  const account: AccountInfo | null = msal.getActiveAccount();
  if (!account) {
    throw new Error('You are signed out. Sign in again to continue.');
  }
  try {
    return (await msal.acquireTokenSilent({ scopes, account })).accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError && interactive) {
      return (await msal.acquireTokenPopup({ scopes, account })).accessToken;
    }
    throw error;
  }
}
